import React, { useEffect, useRef } from "react";
import "./styles.css";
import { StreamingLoading } from "../../screens/streaming-loading";
import { useNavigate, useParams } from "react-router-dom";
import { useClerkAuth } from "../../hooks/use-clerk-auth";
import { useSelectedFiles } from "../../hooks/use-selected-files";
import apiClient from "../../services/api-client";
import config from "../../config";

const AddEvents = () => {
  const { calendar } = useParams();
  const navigate = useNavigate();
  const { user, initDone, getSessionToken } = useClerkAuth();
  const { fileDataMap, clearFileDataMap } = useSelectedFiles();
  const hasProcessed = useRef(false);

  // Job queue approach - no streaming hooks needed anymore

  useEffect(() => {
    console.log("[DEBUG] AddEvents component mounted");
    console.log("[DEBUG] calendar param:", calendar);
    console.log("[DEBUG] hasProcessed:", hasProcessed.current);
    console.log("[DEBUG] window.location.pathname:", window.location.pathname);

    if (!initDone) {
      console.log("[DEBUG] Waiting for authentication to initialize...");
      return;
    }

    // Prevent duplicate processing
    if (hasProcessed.current) {
      console.log("[DEBUG] Already processed, skipping...");
      return;
    }

    // Extract calendar from URL if param is undefined
    let calendarType = calendar;
    if (!calendarType) {
      const pathParts = window.location.pathname.split("/");
      calendarType = pathParts[pathParts.length - 1]; // get last part of URL
      console.log("[DEBUG] Extracted calendar from URL:", calendarType);
    }

    // Start processing immediately when component mounts
    if (calendarType) {
      console.log("[DEBUG] Starting event processing for:", calendarType);
      hasProcessed.current = true; // Mark as processed
      handleAddEventsToCalendar(calendarType);
    } else {
      console.error(
        "[DEBUG] No calendar type found, redirecting to select-calendar"
      );
      navigate("/select-calendar", { replace: true });
    }
  }, [calendar, navigate, initDone]); // Include dependencies that are actually used

  const handleAddEventsToCalendar = async () => {
    if (!calendar) {
      alert("Please select a calendar");
      navigate("/select-calendar", { replace: true });
      return;
    }

    try {
      // First, try to get data from localStorage (for direct flow)
      let rawSelectedSections = localStorage.getItem("selectedSections");
      let rawAllSectionsData = localStorage.getItem("allSectionsData");

      // If localStorage is empty, try sessionStorage (persists through OAuth redirects)
      if (!rawSelectedSections || !rawAllSectionsData) {
        rawSelectedSections =
          rawSelectedSections || sessionStorage.getItem("selectedSections");
        rawAllSectionsData =
          rawAllSectionsData || sessionStorage.getItem("allSectionsData");
      }
      console.log(
        "[DEBUG] Raw selectedSections from localStorage/sessionStorage:",
        rawSelectedSections)
      console.log(
        "[DEBUG] Raw allSectionsData from localStorage/sessionStorage:",
        localStorage.getItem("allSectionsData")
      );
      let courses = [];

      // Check if selectedSections has actual content, not just exists
      const parsedSelectedSections = rawSelectedSections
        ? JSON.parse(rawSelectedSections)
        : {};
      let hasSelectedSections = Object.keys(parsedSelectedSections).length > 0;
     // console.log("[DEBUG] Parsed selectedSections:", hasSelectedSections);

      // If selectedSections is empty but allSectionsData has courses, create default selectedSections
      let selectedSections = parsedSelectedSections;
      //console.log("[DEBUG] Initial selectedSections:", selectedSections);
     //console.log("[DEBUG] rawAllSections data:", rawAllSectionsData);
      if (!hasSelectedSections && rawAllSectionsData) {
       // console.log("[DEBUG] No selectedSections found, creating defaults");
        const allSectionsData = JSON.parse(rawAllSectionsData);
        console.log("[DEBUG] allSectionsData for defaults:", allSectionsData);
        selectedSections = {};
        Object.entries(allSectionsData).forEach(([courseKey, courseData]) => {
          if (courseData.sections && courseData.sections.length > 0) {
            selectedSections[courseKey] = courseData.sections[0]; // Use first section as default
          }
        });
        console.log("[DEBUG] Created default selectedSections:", selectedSections);
        hasSelectedSections = Object.keys(selectedSections).length > 0;
      }

      if (rawAllSectionsData && hasSelectedSections) {
        // Parse localStorage data (normal flow) - selectedSections already prepared above
        const allSectionsData = JSON.parse(rawAllSectionsData);

        // Build courses, filtering out any missing file_data
        const allCourses = Object.entries(selectedSections).map(
          ([courseKey, selectedSection]) => {
            const courseData = allSectionsData[courseKey];
            return {
              course_name: courseData.course_name,
              sections: courseData.sections || [],
              selected_section: {
                pdf_path: courseKey,
                name: selectedSection.name,
                days_of_week: selectedSection.days_of_week,
                timezone: courseData.timezone || config.DEFAULT_TIMEZONE,
                timing: selectedSection.timing,
                location: selectedSection.location || ""
              },
              color: courseData.color,
              file_data: fileDataMap[courseKey] // retrieve file_data from hook state
            };
          }
        );

        // Filter out courses missing file_data (can happen if session data was partially lost)
        courses = allCourses.filter(course => course.file_data);
        const skippedCourses = allCourses.filter(course => !course.file_data);

        if (skippedCourses.length > 0) {
          console.warn("[DEBUG] Skipping courses without file_data:", skippedCourses.map(c => c.course_name));
        }

        // If ALL courses are missing file_data, redirect to error screen
        if (courses.length === 0) {
          navigate("/no-events", {
            replace: true,
            state: {
              courseName: "Your courses",
              errorMessage: "Session data was lost. Please re-upload your syllabi.",
            },
          });
          return;
        }
      } else {
        // No session data available - redirect to error screen
        navigate("/no-events", {
          replace: true,
          state: {
            courseName: "Your courses",
            errorMessage: "Session data was lost. Please re-upload your syllabi.",
          },
        });
        return;
      }
      console.log("[DEBUG] Courses to upload:", courses);

      // Create job (returns immediately)
      const jobResponse = await apiClient.post(
        "/select_section_stream",
        {
          calendar_type: calendar,
          courses: courses,
        },
        getSessionToken
      );

      if (jobResponse.status !== "success") {
        throw new Error(jobResponse.message || "Failed to create job");
      }

      clearFileDataMap(); // Clear file data from hook state after job creation

      const jobId = jobResponse.data.job_id;
      const initialMessage = jobResponse.data.progress_message;

      // Store job_id in localStorage for recovery
      localStorage.setItem("processingJobId", jobId);
      localStorage.setItem("processingCalendar", calendar);

      console.log("[DEBUG] Created job:", jobId);

      // Navigate to extraction loading screen
      navigate("/streaming-loading", {
        state: { jobId, calendar, initialMessage }
      });

  } catch (error) {
    console.error("Error processing events:", error);
    navigate("/no-events", {
      replace: true,
      state: {
        courseName: "Course Name",
        errorMessage: error.message || "Something went wrong while processing your request.",
      },
    });
  }
};

  return <StreamingLoading />;
};
export default AddEvents;
